'use client';

import { useEffect, useRef, useState, type ComponentProps, type RefObject } from 'react';

import { ChevronDown, Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { DOC_SECTIONS } from '@/app/[tenant]/mcp/docs/sections';
import { MARKETING_ROUTES, PLATFORM_URL } from '@/common/constants';
import { DemoLink } from '@/components/demo-link';
import { HighlightItem, HoverHighlight } from '@/components/hover-highlight';
import { LogoLink } from '@/components/logo-link';
import { UI_EASE } from '@/components/reveal';
import { ContentWidth, GUTTER_X } from '@/components/section';
import { SmartLink } from '@/components/smart-link';
import { Button } from '@/components/ui/button';
import { INDUSTRIES } from '@/lib/content';
import { cn, FOCUS_RING_INSET, NAV_LINK_STYLE } from '@/lib/utils';

import { ThemeToggleButton } from './theme-toggle-button';

/**
 * Homepage section order. `SmartLink` reads the hash off each href, so a `/#section` entry scrolls
 * in place on `/` and navigates from anywhere else.
 *
 * Industries is a route, not a hash: there is no `#industries` band on the homepage, and claiming
 * one turns the link into a no-op there — `SmoothHashScroll` bails on a missing target without
 * preventing the default, and the browser's default for an unmatched hash is to do nothing.
 */
const NAV_LINKS = [
  { label: 'Platform', href: MARKETING_ROUTES.platformHash },
  { label: 'How it works', href: MARKETING_ROUTES.howItWorks },
  { label: 'Industries', href: MARKETING_ROUTES.industries },
  { label: 'Security', href: MARKETING_ROUTES.security },
  { label: 'News', href: MARKETING_ROUTES.news },
  { label: 'Pricing', href: MARKETING_ROUTES.pricing },
] as const;

const BAR_LINK = cn('text-sm font-medium whitespace-nowrap', NAV_LINK_STYLE);

const SHEET_LINK = cn(
  'block rounded-lg px-3 py-2.5 text-lg font-medium transition-colors duration-200',
  FOCUS_RING_INSET,
);

const SHEET_SUB_LINK = cn('block rounded-lg px-3 py-2 text-base', NAV_LINK_STYLE, FOCUS_RING_INSET);

/**
 * Hand-copied from `--breakpoint-nav` in globals.css, which the `nav:` variants below compile
 * from. These two CAN drift — Tailwind resolves the variants at build time from the theme block,
 * and nothing checks this against it. Change one, change the other.
 */
const NAV_BREAKPOINT = '1120px';

function MenuButton({
  open,
  onToggle,
  buttonRef,
  className,
}: {
  open: boolean;
  onToggle: () => void;
  buttonRef: RefObject<HTMLButtonElement | null>;
  className?: string;
}) {
  return (
    <Button
      ref={buttonRef}
      variant="ghost"
      size="icon"
      onClick={onToggle}
      aria-label={open ? 'Close menu' : 'Open menu'}
      aria-expanded={open}
      aria-controls="mobile-menu"
      className={cn('relative', className)}
    >
      <Menu
        aria-hidden="true"
        className={cn(
          'absolute h-5 w-5 transition-all duration-350',
          open ? 'scale-0 rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100',
        )}
      />
      <X
        aria-hidden="true"
        className={cn(
          'absolute h-5 w-5 transition-all duration-350',
          open ? 'scale-100 rotate-0 opacity-100' : 'scale-0 -rotate-90 opacity-0',
        )}
      />
    </Button>
  );
}

function NavHref({
  link,
  children,
  ...rest
}: { link: (typeof NAV_LINKS)[number] } & Omit<ComponentProps<'a'>, 'href'>) {
  return (
    <SmartLink href={link.href} {...rest}>
      {children ?? link.label}
    </SmartLink>
  );
}

export function Header() {
  const pathname = usePathname();
  const isConnectorDocs = /\/mcp\/docs\/?$/.test(pathname);
  const [isOpen, setIsOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const menuRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const industriesRef = useRef<HTMLDivElement>(null);
  const industriesTriggerRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      const firstFocusable = menuRef.current?.querySelector<HTMLElement>(
        'a, button, input, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
      wasOpenRef.current = true;
      document.body.style.overflow = 'hidden';
    } else if (wasOpenRef.current) {
      menuButtonRef.current?.focus();
      wasOpenRef.current = false;
    }
    // `inert`, not a focus trap: content behind the sheet must be unreachable, unfocusable and
    // unread, which is the whole contract — a trap only covers the Tab half of it.
    const behind = document.getElementById('main-content');
    const footer = document.querySelector('footer');
    for (const region of [behind, footer]) region?.toggleAttribute('inert', isOpen);
    return () => {
      document.body.style.overflow = '';
      for (const region of [behind, footer]) region?.removeAttribute('inert');
    };
  }, [isOpen]);

  // Past the breakpoint CSS hides the sheet, but `isOpen` stays true and the scroll lock above
  // never releases. A listener rather than `useMediaQuery`: this has to set state.
  useEffect(() => {
    const wide = window.matchMedia(`(min-width: ${NAV_BREAKPOINT})`);
    function closeIfWide(event: MediaQueryList | MediaQueryListEvent) {
      if (event.matches) setIsOpen(false);
    }
    closeIfWide(wide);
    wide.addEventListener('change', closeIfWide);
    return () => wide.removeEventListener('change', closeIfWide);
  }, []);

  useEffect(() => {
    if (!isOpen && !industriesOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // The disclosure that is actually open takes the dismissal, and focus returns to the trigger
      // that opened it — the Industries button for the panel, the hamburger for the sheet.
      if (industriesOpen) {
        setIndustriesOpen(false);
        industriesTriggerRef.current?.focus();
        return;
      }
      setIsOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, industriesOpen]);

  useEffect(() => {
    if (!industriesOpen) return;
    function handlePointer(e: PointerEvent) {
      if (!industriesRef.current?.contains(e.target as Node)) {
        setIndustriesOpen(false);
      }
    }
    document.addEventListener('pointerdown', handlePointer);
    return () => document.removeEventListener('pointerdown', handlePointer);
  }, [industriesOpen]);

  /**
   * Closing because the visitor is going somewhere, not because they dismissed the sheet.
   *
   * `SmoothHashScroll` focuses the destination section on the same click, and the effect above
   * would then pull focus back to the trigger — so a keyboard user activating a section link stays
   * at the header and Tab does not continue from where they landed. Escape and the close button
   * still restore the trigger, which is where focus belongs on a dismissal.
   */
  function closeMenuForNavigation() {
    wasOpenRef.current = false;
    closeMenu();
  }

  function closeMenu() {
    setIsOpen(false);
    setIndustriesOpen(false);
  }

  return (
    <>
      {/* Opaque on purpose — see the backdrop-filter gotcha in CLAUDE.md. */}
      <header className="fixed top-0 z-50 w-full border-b bg-nav">
        <ContentWidth className="flex h-header items-center">
          <LogoLink className="me-8" onNavigate={closeMenuForNavigation} />
          {isConnectorDocs ? (
            <>
              <nav aria-label="On this page" className="hidden items-center space-x-6 nav:flex">
                {Object.values(DOC_SECTIONS).map(section => (
                  <SmartLink key={section.id} href={`#${section.id}`} className={BAR_LINK}>
                    {section.title}
                  </SmartLink>
                ))}
              </nav>
              <div className="flex flex-1 items-center justify-end gap-4">
                <ThemeToggleButton />
                <MenuButton
                  className="nav:hidden"
                  open={isOpen}
                  onToggle={() => setIsOpen(prev => !prev)}
                  buttonRef={menuButtonRef}
                />
              </div>
            </>
          ) : (
            <>
              <nav
                aria-label="Main navigation"
                className="hidden flex-1 items-center gap-6 nav:flex"
              >
                {NAV_LINKS.map(link =>
                  link.label === 'Industries' ? (
                    <div
                      key={link.label}
                      ref={industriesRef}
                      className="relative"
                      // Opens on hover as well as click. The panel's own wrapper carries the gap
                      // as padding rather than margin, so crossing from the trigger to the menu
                      // never leaves the hoverable area.
                      onMouseEnter={() => setIndustriesOpen(true)}
                      onMouseLeave={() => setIndustriesOpen(false)}
                    >
                      <button
                        type="button"
                        ref={industriesTriggerRef}
                        aria-expanded={industriesOpen}
                        onClick={() => setIndustriesOpen(open => !open)}
                        className={cn(
                          'inline-flex cursor-pointer items-center gap-1 whitespace-nowrap',
                          'text-sm font-medium',
                          NAV_LINK_STYLE,
                          industriesOpen && 'text-foreground',
                        )}
                      >
                        Industries
                        <ChevronDown
                          aria-hidden="true"
                          className={cn(
                            'h-3.5 w-3.5 transition-transform duration-200',
                            industriesOpen && 'rotate-180',
                          )}
                        />
                      </button>
                      <AnimatePresence>
                        {industriesOpen && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.96, y: -6 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.96, y: -6 }}
                            transition={{ duration: 0.2, ease: UI_EASE }}
                            // origin-top so it unfolds from under the trigger instead of
                            // swelling from its own middle.
                            className="absolute start-0 top-full z-50 origin-top pt-3"
                          >
                            <HoverHighlight className="min-w-56 rounded-xl border bg-card p-2 shadow-lg">
                              <HighlightItem>
                                <NavHref
                                  link={link}
                                  onClick={closeMenuForNavigation}
                                  className={cn(
                                    'block rounded-lg px-3 py-2 text-sm font-medium',
                                    NAV_LINK_STYLE,
                                  )}
                                >
                                  All industries
                                </NavHref>
                              </HighlightItem>
                              {INDUSTRIES.map(industry => (
                                <HighlightItem key={industry.slug}>
                                  <Link
                                    href={industry.href}
                                    onClick={closeMenuForNavigation}
                                    className={cn(
                                      'block rounded-lg px-3 py-2 text-sm',
                                      NAV_LINK_STYLE,
                                    )}
                                  >
                                    {industry.label}
                                  </Link>
                                </HighlightItem>
                              ))}
                            </HoverHighlight>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <NavHref
                      key={link.label}
                      link={link}
                      onClick={closeMenuForNavigation}
                      className={BAR_LINK}
                    />
                  ),
                )}
              </nav>
              {/* Log in is account utility, not a third CTA — it sits with the nav links, behind a
                  divider, so only the two conversion actions read as a set. Those two are outline
                  vs filled rather than ghost vs filled: a wide enough gap to read as a rank. */}
              <div className="hidden flex-1 items-center justify-end gap-3 nav:flex">
                <a href={PLATFORM_URL} className={BAR_LINK}>
                  Log in
                </a>
                <span aria-hidden="true" className="h-5 w-px bg-border" />
                <Button variant="outline" size="default" asChild>
                  <DemoLink>Get a demo</DemoLink>
                </Button>
                <Button size="default" asChild>
                  <Link href={MARKETING_ROUTES.scan}>Scan your brand</Link>
                </Button>
                <ThemeToggleButton />
              </div>

              {/* The toggle lives in the bar, not down in the sheet: it is a global control, not
                  one of the navigation actions. */}
              {/* gap-4, not gap-1: the toggle's 44px hit area already reaches most of the way to
                  the menu button, so a tight gap put two different controls under one thumb. */}
              <div className="flex flex-1 items-center justify-end gap-4 nav:hidden">
                <ThemeToggleButton />
                <MenuButton
                  open={isOpen}
                  onToggle={() => setIsOpen(prev => !prev)}
                  buttonRef={menuButtonRef}
                />
              </div>
            </>
          )}
        </ContentWidth>
      </header>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: UI_EASE }}
            className="fixed inset-0 z-40 bg-background nav:hidden"
            style={{ top: 'var(--spacing-header)' }}
          >
            <nav
              ref={menuRef}
              id="mobile-menu"
              aria-label="Mobile navigation"
              // Gutter matches ContentWidth so the menu items line up under the logo.
              className={cn('flex h-full flex-col py-6 pb-8', GUTTER_X)}
            >
              {/* Negative margin so the row's padding bleeds into the gutter and the labels
                still line up under the logo. */}
              <HoverHighlight className="-mx-3 flex-1 space-y-1 overflow-y-auto">
                {isConnectorDocs &&
                  Object.values(DOC_SECTIONS).map(section => (
                    <HighlightItem key={section.id}>
                      <SmartLink
                        href={`#${section.id}`}
                        onClick={closeMenuForNavigation}
                        className={SHEET_LINK}
                      >
                        {section.title}
                      </SmartLink>
                    </HighlightItem>
                  ))}
                {!isConnectorDocs &&
                  NAV_LINKS.map(link =>
                    link.label === 'Industries' ? (
                      <div key={link.label}>
                        <HighlightItem>
                          <NavHref
                            link={link}
                            onClick={closeMenuForNavigation}
                            className={cn(
                              'block rounded-lg px-3 py-2.5 text-lg font-medium transition-colors duration-200',
                              FOCUS_RING_INSET,
                            )}
                          />
                        </HighlightItem>
                        <div className="ms-6 border-s ps-3">
                          {INDUSTRIES.map(industry => (
                            <HighlightItem key={industry.slug}>
                              <Link
                                href={industry.href}
                                onClick={closeMenuForNavigation}
                                className={SHEET_SUB_LINK}
                              >
                                {industry.label}
                              </Link>
                            </HighlightItem>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <HighlightItem key={link.label}>
                        <NavHref
                          link={link}
                          onClick={closeMenuForNavigation}
                          className={SHEET_LINK}
                        />
                      </HighlightItem>
                    ),
                  )}
              </HoverHighlight>
              {/* Same rank as the bar — filled, outline, ghost — but all three as Buttons so they
                  share one radius and one height.

                  h-11 rather than the h-9 default: these are the one place the buttons are finger
                  targets rather than cursor targets, and h-9 is 36px against a 44px floor. */}
              <div className={cn('mt-6 space-y-3 border-t pt-6', isConnectorDocs && 'hidden')}>
                <Button size="default" className="h-11 w-full" asChild>
                  <Link href={MARKETING_ROUTES.scan} onClick={closeMenuForNavigation}>
                    Scan your brand
                  </Link>
                </Button>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="default" className="h-11 flex-1" asChild>
                    <DemoLink onClick={closeMenuForNavigation}>Get a demo</DemoLink>
                  </Button>
                  <Button variant="ghost" size="default" className="h-11 flex-1" asChild>
                    <a href={PLATFORM_URL} onClick={closeMenuForNavigation}>
                      Log in
                    </a>
                  </Button>
                </div>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
